import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  Autocomplete,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useProgramSessionDates } from "../../../hooks/useProgramSessionDates";
import { getAllUsers } from "../../../apis/userApi";
import { useQuery } from "@tanstack/react-query";
import { manageUsersQueryOptions } from "../../../queryOptions/ManageBookingsOptions";
import { isAdultAgeGroup } from "../../../utils/ageGroupUtils";

interface AddEnrollmentDialogProps {
  open: boolean;
  selectedSession: any;
  sessions: any[];
  onClose: () => void;
  onCreate: (withCheckout: boolean, data: any, checkoutWindow?: Window | null) => Promise<void>;
}

export const AddEnrollmentDialog = ({ open, selectedSession, sessions, onClose, onCreate }: AddEnrollmentDialogProps) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [creating, setCreating] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(true);
  const [participantSameAsUser, setParticipantSameAsUser] = useState(false);
  const [guardianSameAsUser, setGuardianSameAsUser] = useState(false);
  const [emergencySameAsUser, setEmergencySameAsUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    participantName: "",
    parentGuardianName: "",
    participantAge: "",
    parentGuardianContact: "",
    emergencyContact: "",
    address: "",
    participantContact: "",
    medicalConditions: "",
    selectedStartDate: "",
    timeSlot: "",
    tax: "",
    paymentMethod: "pay_at_counter",
  });

  const { data: allUsers, refetch } = useQuery({
    ...manageUsersQueryOptions(),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const userDetails = allUsers?.filter((user: any) => user.role === "user") || [];

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  const { dateOptions, timeSlots } = useProgramSessionDates(
    selectedSession || null,
    form.selectedStartDate
  );

  const isAdultParticipant = isAdultAgeGroup(form.participantAge);

  const userFullName = (firstName: string, lastName: string) =>
    `${firstName} ${lastName}`.trim();

  const onChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "participantAge") {
      const becomingAdult = isAdultAgeGroup(value);
      const becomingMinor = !!value && !becomingAdult;
      setForm((prev) => ({
        ...prev,
        participantAge: value,
        ...(becomingAdult
          ? {
              parentGuardianName: "",
              parentGuardianContact: "",
              ...(participantSameAsUser ? { participantContact: prev.phoneNumber } : {}),
            }
          : {}),
        ...(becomingMinor
          ? {
              participantContact: "",
              ...(guardianSameAsUser
                ? {
                    parentGuardianName: userFullName(prev.firstName, prev.lastName),
                    parentGuardianContact: prev.phoneNumber,
                  }
                : {}),
            }
          : {}),
      }));
      return;
    }
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      const firstName = name === "firstName" ? value : prev.firstName;
      const lastName = name === "lastName" ? value : prev.lastName;
      const phoneNumber = name === "phoneNumber" ? value : prev.phoneNumber;
      if (!isExistingUser && participantSameAsUser && (name === "firstName" || name === "lastName" || name === "phoneNumber")) {
        next.participantName = userFullName(firstName, lastName);
        if (isAdultAgeGroup(prev.participantAge)) {
          next.participantContact = phoneNumber;
        }
      }
      if (!isExistingUser && guardianSameAsUser && (name === "firstName" || name === "lastName" || name === "phoneNumber")) {
        next.parentGuardianName = userFullName(firstName, lastName);
        next.parentGuardianContact = phoneNumber;
      }
      if (!isExistingUser && emergencySameAsUser && name === "phoneNumber") {
        next.emergencyContact = phoneNumber;
      }
      return next;
    });
  };

  // Handle user selection for existing users
  const handleUserSelect = (event: any, newValue: any) => {
    setSelectedUser(newValue);
    if (newValue) {
      const user = userDetails?.find((user: any) => user.id === newValue.id);
      if (user) {
        // Auto-fill participant info from user data
        setForm(prev => ({
          ...prev,
          email: user.email,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          phoneNumber: user.phoneNumber || "",
          address: user.address || "",
          participantName: "",
          parentGuardianName: `${user.firstName} ${user.lastName}`,
          parentGuardianContact: user.phoneNumber || "",
          emergencyContact: "",
        }));
      }
    } else {
      // Clear form if no user selected
      setForm(prev => ({
        ...prev,
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        participantName: "",
        parentGuardianName: "",
        parentGuardianContact: "",
        emergencyContact: "",
        address: "",
      }));
    }
  };

  const resetForm = () => {
    setForm({
      email: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      participantName: "",
      parentGuardianName: "",
      participantAge: "",
      parentGuardianContact: "",
      emergencyContact: "",
      address: "",
      participantContact: "",
      medicalConditions: "",
      selectedStartDate: "",
      timeSlot: "",
      tax: "",
      paymentMethod: "pay_at_counter",
    });
    setIsExistingUser(true);
    setParticipantSameAsUser(false);
    setGuardianSameAsUser(false);
    setEmergencySameAsUser(false);
    setSelectedUser(null);
  };

  const handleCreate = async (withCheckout: boolean) => {
    setCreating(true);
    const checkoutWindow = withCheckout ? window.open("about:blank", "_blank") : null;
    try {
      const enrollmentData: any = {
        ...form,
        isExistingUser,
      };

      if (isAdultParticipant) {
        enrollmentData.parentGuardianName = "";
        enrollmentData.parentGuardianContact = "";
      } else {
        enrollmentData.participantContact = "";
      }

      // Only include tax percentage for direct enrollment (without checkout)
      if (!withCheckout) {
        enrollmentData.tax = parseFloat(form.tax) || 0;
      }

      await onCreate(withCheckout, enrollmentData, checkoutWindow);
      resetForm();
      onClose();
    } catch (error: any) {
      checkoutWindow?.close();
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to create enrollment",
        severity: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const goesToCheckout = form.paymentMethod === "checkout";

  const isSubmitDisabled =
    creating ||
    !form.participantName.trim() ||
    !form.email.trim() ||
    !form.emergencyContact.trim() ||
    !form.participantAge ||
    !form.selectedStartDate ||
    !form.timeSlot ||
    !form.paymentMethod ||
    (!isExistingUser && (!form.firstName.trim() || !form.lastName.trim() || !form.phoneNumber.trim())) ||
    (isExistingUser && !selectedUser);

  return (
    <>
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700} component="div">
            Add Program Enrollment
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* User Type Selection */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExistingUser}
                  onChange={(e) => {
                    setIsExistingUser(e.target.checked);
                    setParticipantSameAsUser(false);
                    setGuardianSameAsUser(false);
                    setEmergencySameAsUser(false);
                    if (!e.target.checked) {
                      // Clear auto-filled data when switching to new user
                      setForm(prev => ({
                        ...prev,
                        firstName: "",
                        lastName: "",
                        phoneNumber: "",
                        participantName: "",
                        parentGuardianName: "",
                        parentGuardianContact: "",
                        emergencyContact: "",
                        address: "",
                        email: "",
                      }));
                      setSelectedUser(null);
                    } else {
                      // Clear form when switching to existing user
                      setForm(prev => ({
                        ...prev,
                        firstName: "",
                        lastName: "",
                        phoneNumber: "",
                        participantName: "",
                        parentGuardianName: "",
                        parentGuardianContact: "",
                        emergencyContact: "",
                        address: "",
                        email: "",
                      }));
                      setSelectedUser(null);
                    }
                  }}
                />
              }
              label="Existing User"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {isExistingUser 
                ? "Enter email to auto-fill participant information" 
                : "Fill in all required user information to create a new user"
              }
            </Typography>
          </Box>

          {/* User Information */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              User Information
            </Typography>
            <Grid container spacing={2}>
              {isExistingUser ? (
                <Grid item xs={12}>
                  <Autocomplete
                    disablePortal
                    options={userDetails?.map((user: any) => {
                      return {
                        id: user.id,
                        label: `${user.firstName} ${user.lastName} (${user.email})`,
                      };
                    }) || []}
                    getOptionLabel={(option: any) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedUser}
                    onChange={handleUserSelect}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Existing User"
                        variant="outlined"
                        fullWidth
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    sx={{
                      mb: 3,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "background.paper",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                          borderColor: "primary.main",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiAutocomplete-inputRoot": {
                        paddingLeft: "16px !important",
                      },
                    }}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Email" 
                    name="email" 
                    type="email"
                    value={form.email} 
                    onChange={onChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              )}
              
              {!isExistingUser && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="First Name" 
                      name="firstName" 
                      value={form.firstName} 
                      onChange={onChange}
                      required={!isExistingUser}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="Last Name" 
                      name="lastName" 
                      value={form.lastName} 
                      onChange={onChange}
                      required={!isExistingUser}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="Phone Number" 
                      name="phoneNumber" 
                      value={form.phoneNumber} 
                      onChange={onChange}
                      required={!isExistingUser}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Participant Information
            </Typography>
            {!isExistingUser && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={participantSameAsUser}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setParticipantSameAsUser(checked);
                      if (checked) {
                        setForm((prev) => ({
                          ...prev,
                          participantName: userFullName(prev.firstName, prev.lastName),
                          ...(isAdultParticipant ? { participantContact: prev.phoneNumber } : {}),
                        }));
                      }
                    }}
                  />
                }
                label="Participant information is the same as the user"
                sx={{ mb: 1 }}
              />
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Participant Name" 
                  name="participantName" 
                  value={form.participantName} 
                  onChange={onChange}
                  required
                  disabled={!isExistingUser && participantSameAsUser}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Participant Email" 
                  name="email" 
                  type="email"
                  value={form.email} 
                  onChange={onChange}
                  required
                  disabled={!isExistingUser && participantSameAsUser}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Participant Age Group"
                  name="participantAge"
                  value={form.participantAge}
                  onChange={onChange}
                  required
                  helperText="Select the age group for this participant"
                >
                  {(() => {
                    const slotGroups: string[] = selectedSession?.slotAgeGroups?.[form.timeSlot] || [];
                    const base = selectedSession?.ageGroups || [];
                    const groups = slotGroups.length ? slotGroups : base;
                    return groups.length ? groups.map((a: string) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    )) : <MenuItem disabled>No age groups available</MenuItem>;
                  })()}
                </TextField>
              </Grid>
              {isAdultParticipant && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact"
                    name="participantContact"
                    value={form.participantContact}
                    onChange={onChange}
                    disabled={!isExistingUser && participantSameAsUser}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {!isAdultParticipant && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Guardian Information
            </Typography>
            {!isExistingUser && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={guardianSameAsUser}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setGuardianSameAsUser(checked);
                      if (checked) {
                        setForm((prev) => ({
                          ...prev,
                          parentGuardianName: userFullName(prev.firstName, prev.lastName),
                          parentGuardianContact: prev.phoneNumber,
                        }));
                      }
                    }}
                  />
                }
                label="Guardian information is the same as the user"
                sx={{ mb: 1 }}
              />
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Parent / Guardian Name" 
                  name="parentGuardianName" 
                  value={form.parentGuardianName} 
                  onChange={onChange}
                  disabled={!isExistingUser && guardianSameAsUser}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Guardian Contact" 
                  name="parentGuardianContact" 
                  value={form.parentGuardianContact} 
                  onChange={onChange}
                  disabled={!isExistingUser && guardianSameAsUser}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          )}

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Additional Information
            </Typography>
            {!isExistingUser && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={emergencySameAsUser}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEmergencySameAsUser(checked);
                      if (checked) {
                        setForm((prev) => ({
                          ...prev,
                          emergencyContact: prev.phoneNumber,
                        }));
                      }
                    }}
                  />
                }
                label="Emergency contact is the same as the user"
                sx={{ mb: 1 }}
              />
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Emergency Contact" 
                  name="emergencyContact" 
                  value={form.emergencyContact} 
                  onChange={onChange}
                  required
                  disabled={!isExistingUser && emergencySameAsUser}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              {!isAdultParticipant && (
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Home Address" 
                  name="address" 
                  value={form.address} 
                  onChange={onChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              )}
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  multiline 
                  rows={3}
                  label="Medical Conditions / Allergies" 
                  name="medicalConditions" 
                  value={form.medicalConditions} 
                  onChange={onChange}
                  placeholder="Please list any medical conditions, allergies, or special considerations..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                        <MedicalServicesIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Enrollment Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Select Start Date"
                  name="selectedStartDate"
                  value={form.selectedStartDate}
                  onChange={(e) => {
                    const date = e.target.value;
                    // compute end date using overrides if available
                    let endDate: string | undefined = undefined;
                    if (selectedSession?.dateEndOverrides && selectedSession.dateEndOverrides[date]) {
                      endDate = selectedSession.dateEndOverrides[date];
                    } else {
                      // fall back to existing logic in the backend; we only need start date here
                    }
                    setForm(prev => ({ ...prev, selectedStartDate: date, timeSlot: "", participantAge: prev.participantAge }));
                  }}
                  required
                  disabled={!dateOptions.length}
                  helperText={!dateOptions.length && selectedSession ? "No available dates for this session" : ""}
                >
                  {dateOptions.map((date) => (
                    <MenuItem key={date} value={date}>
                      {new Date(date + "T00:00:00").toLocaleDateString([], {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Select Time Slot"
                  name="timeSlot"
                  value={form.timeSlot}
                  onChange={onChange}
                  required
                  disabled={!timeSlots.length || !form.selectedStartDate}
                  helperText={!form.selectedStartDate ? "Please select a date first" : !timeSlots.length ? "No time slots available" : ""}
                >
                  {timeSlots.map((slot) => {
                    const cap = selectedSession?.slotCapacities?.[slot];
                    let perSlotText = slot.replace("-", " - ");
                    let disabled = false;
                    if (typeof cap === "number") {
                      perSlotText += ` (cap ${cap})`;
                    }
                    return (
                      <MenuItem key={slot} value={slot} disabled={disabled}>
                        {perSlotText}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Payment Method"
                  name="paymentMethod"
                  value={form.paymentMethod}
                  onChange={onChange}
                  required
                >
                  <MenuItem value="pay_at_counter">Pay at counter</MenuItem>
                  <MenuItem value="checkout">Checkout</MenuItem>
                </TextField>
              </Grid>
              
              {/* Tax percentage field - only show for direct enrollment (without checkout) */}
              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tax Percentage (%)"
                  name="tax"
                  type="number"
                  value={form.tax}
                  onChange={onChange}
                  placeholder="15"
                  inputProps={{ step: "0.1", min: "0", max: "100" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary">%</Typography>
                      </InputAdornment>
                    )
                  }}
                  helperText="Enter tax percentage for direct enrollment (not applicable for checkout)"
                />
              </Grid> */}
              
              {/* Calculated total display */}
              {/* <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2, 
                  border: "1px solid", 
                  borderColor: "divider", 
                  borderRadius: 1, 
                  backgroundColor: "background.paper" 
                }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Pricing Breakdown
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2">Program Price:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${Number(selectedSession?.program?.price || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  {form.tax && parseFloat(form.tax) > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="body2">Tax ({form.tax}%):</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${(Number(selectedSession?.program?.price || 0) * parseFloat(form.tax) / 100).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle2" fontWeight={700}>Total Amount:</Typography>
                    <Typography variant="subtitle2" fontWeight={700} color="primary">
                      ${(Number(selectedSession?.program?.price || 0) + (Number(selectedSession?.program?.price || 0) * (parseFloat(form.tax) || 0) / 100)).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Grid> */}
            </Grid>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button onClick={handleClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        {/* <Button 
          disabled={creating || !form.participantName || !form.email || !form.selectedStartDate || !form.timeSlot || (!isExistingUser && (!form.firstName || !form.lastName || !form.phoneNumber)) || (isExistingUser && !selectedUser)} 
          onClick={() => handleCreate(false)} 
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Create Only
        </Button> */}
        <Button 
          disabled={isSubmitDisabled} 
          onClick={() => handleCreate(goesToCheckout)} 
          variant="contained"
          startIcon={goesToCheckout ? <ShoppingCartIcon /> : undefined}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {goesToCheckout ? "Create & Checkout" : "Create Enrollment"}
        </Button>
      </DialogActions>
    </Dialog>
         <Snackbar
         open={snackbar.open}
         autoHideDuration={4000}
         onClose={() => setSnackbar({ ...snackbar, open: false })}
         anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
       >
         <Alert
           onClose={() => setSnackbar({ ...snackbar, open: false })}
           severity={snackbar.severity}
           sx={{ borderRadius: 2 }}
         >
           {snackbar.message}
         </Alert>
       </Snackbar>
       </>
  );
};

